import { useEffect } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, driverApi } from '../lib/api.ts';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { useBranding } from '../lib/branding.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import type { Driver } from '../lib/types.ts';

type DriverWithCompany = Driver & {
  company: { id: string; company_name: string; contact_name: string } | null;
};

export function DriverShell() {
  const { session, loading, signOut } = useCompanyAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const brandName = branding.companyName?.replace(/ Safety.*$/i, '') ?? 'Locate2u';

  const { data, isLoading, error } = useQuery({
    queryKey: ['driver-me'],
    queryFn: () => driverApi.get<{ driver: DriverWithCompany }>('/api/driver/me'),
    enabled: !!session,
  });

  useEffect(() => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      void signOut();
      navigate('/c/expired', { replace: true });
    }
  }, [error, signOut, navigate]);

  if (loading) return <CenteredSpinner />;
  if (!session) {
    return <Navigate to="/c/expired" state={{ from: location }} replace />;
  }
  if (isLoading) return <CenteredSpinner />;
  const driver = data?.driver;

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b bg-white" style={{ borderColor: '#E2E8F0' }}>
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
              style={{ background: 'var(--brand-primary)' }}
            >
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={brandName} className="h-5 w-auto" />
              ) : (
                <span className="text-sm font-bold text-white">
                  {brandName.slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <div
                className="text-[15px] font-bold leading-tight tracking-tight"
                style={{ color: 'var(--brand-primary)' }}
              >
                Driver onboarding
              </div>
              {driver && (
                <div className="text-xs text-slate-500">
                  {driver.name}
                  {driver.company?.company_name ? ` · ${driver.company.company_name}` : ''}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {driver && (
              <div className="hidden sm:block">
                <CompanyStatusBadge status={driver.status} />
              </div>
            )}
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>
        {driver && (
          <div className="mx-auto max-w-5xl px-6 pb-4">
            <ProgressBar value={driver.progress} label="Progress" />
          </div>
        )}
        <nav className="mx-auto flex max-w-5xl gap-1 px-6">
          {[
            { to: '/d/questionnaire', label: 'Questionnaire' },
            { to: '/d/training', label: 'Training' },
            { to: '/d/library', label: 'Library' },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `border-b-2 px-3 py-2.5 text-sm transition ${
                  isActive ? 'font-semibold' : 'border-transparent font-medium'
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? { borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }
                  : { color: '#475569' }
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
